/**
 * `PATCH /triagem/{ticketId}/campos/{campoId}` (`TP-030`) — a escrita que
 * confirma/corrige um campo lido com baixa confiança.
 *
 * `requisitar` já traduz qualquer `!ok` em `ErroDaApi` com `.status`
 * preenchido (`shared/api/clienteHttp.ts`) — este módulo não intercepta
 * nada: quem chama distingue `400` (campo clínico, ou `valorHumano` vazio),
 * `404` (campo/ticket que a sessão não alcança), `409` (já revisado por
 * outra pessoa) e `401` (sessão expirada) pelo `.status` do erro, mesmo
 * padrão que `buscarDetalhe`/`buscarEmailOriginal` já seguem para o `404`
 * deles — a diferença aqui é que NENHUM desses status vira sucesso
 * silencioso: os quatro são estados distintos que a tela precisa mostrar
 * (`T5`).
 */

import type { CampoDaTriagemConfirmado } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';

/** O campo e o ticket confirmados — eco do que a API já sabia, sem PII. */
export interface CampoConfirmado {
  readonly ticketId: string;
  readonly campoId: string;
}

function mapearCampoConfirmado(resposta: CampoDaTriagemConfirmado): CampoConfirmado {
  return { ticketId: resposta.ticketId, campoId: resposta.campoId };
}

/**
 * Grava a correção humana de um campo. Lança `ErroDaApi` em qualquer
 * falha — não há caminho de sucesso silencioso aqui (ao contrário de
 * `buscarDetalhe`), porque toda falha desta escrita é algo que a pessoa
 * revisando precisa ver.
 */
export function confirmarCampo(
  ticketId: string,
  campoId: string,
  valorHumano: string,
  sinal?: AbortSignal,
): Promise<CampoConfirmado> {
  return requisitar<CampoDaTriagemConfirmado>(
    `/triagem/${encodeURIComponent(ticketId)}/campos/${encodeURIComponent(campoId)}`,
    { method: 'PATCH', corpo: { valorHumano }, sinal },
  ).then(mapearCampoConfirmado);
}
